import { max } from 'd3-array';

import { ModelSetTransformer, ModelTransformer } from '../common/common.model';

export interface ExtDualChartPerformanceComparisonPillar {
    pillarID: number;
    pillarTitle: string;
    barTitle: string;
    percentToTarget: number;
    kpiPosition: number;
    displayOrder: number;
    actual: number;
    targetValue: number;
    rankOrder: number;
    trendClass: string;
    thresholdClass: string;
    thresholdTitle: string;
}

export interface Pillar {
    title: string;
    actual: number;
    targetValue: number;
    trendClass: string;
    thresholdClass: string;
    thresholdTitle: string;
}

export interface PillarComparison {
    piePillar: Pillar;
    d3: { options?: any; data: any };
}

export interface DualChartPerformanceComparisonPillar extends Pillar {
    pillarID: number;
    barTitle: string;
    percentToTarget: number;
    kpiPosition: number;
    displayOrder: number;
}

export interface DualChartPerformanceComparison extends PillarComparison {
    barPillars: DualChartPerformanceComparisonPillar[];
}

class DualChartPerformanceComparisonPillarModelTransformer
    implements
        ModelTransformer<
            DualChartPerformanceComparisonPillar,
            ExtDualChartPerformanceComparisonPillar
        >
{
    transform(
        model: ExtDualChartPerformanceComparisonPillar,
    ): DualChartPerformanceComparisonPillar {
        return {
            pillarID: model.pillarID,
            title: model.pillarTitle,
            barTitle: model.barTitle,
            actual: model.actual,
            targetValue: model.targetValue,
            percentToTarget: model.percentToTarget,
            trendClass: model.trendClass,
            thresholdClass: model.thresholdClass,
            thresholdTitle: model.thresholdTitle,
            kpiPosition: model.kpiPosition,
            displayOrder: model.displayOrder,
        };
    }
}

class DualChartPerformanceComparisonPillarModelSetTransformer extends ModelSetTransformer<
    DualChartPerformanceComparisonPillar,
    ExtDualChartPerformanceComparisonPillar
> {
    protected newTransformer(): ModelTransformer<
        DualChartPerformanceComparisonPillar,
        ExtDualChartPerformanceComparisonPillar
    > {
        return new DualChartPerformanceComparisonPillarModelTransformer();
    }

    transform(
        model: ExtDualChartPerformanceComparisonPillar[],
    ): DualChartPerformanceComparisonPillar[] {
        return super.transform(model);
    }
}

export class DualChartPerformanceComparisonModelTransformer
    implements
        ModelTransformer<
            DualChartPerformanceComparison[],
            ExtDualChartPerformanceComparisonPillar[]
        >
{
    transform(
        model: ExtDualChartPerformanceComparisonPillar[],
    ): DualChartPerformanceComparison[] {
        const pillars =
            new DualChartPerformanceComparisonPillarModelSetTransformer().transform(
                model,
            );

        //  pie chart uses one pillar. assume number of sets of pillars === number of pillars with barTitle === doughnut
        const pillarSetCount = pillars.reduce(
            (previousValue, currentValue) =>
                previousValue +
                (currentValue.barTitle.toLowerCase() === 'doughnut' ? 1 : 0),
            0,
        );

        const transformed: DualChartPerformanceComparison[] = [];
        for (let i = 0; i < pillarSetCount; i++) {
            const pillar = pillars[i];
            const barPillarSet = pillars
                .filter(
                    (value) =>
                        value &&
                        value.pillarID === pillar.pillarID &&
                        value.barTitle.toLowerCase() !== 'doughnut',
                )
                .sort((a, b) => a.displayOrder - b.displayOrder);

            transformed.push({
                piePillar: pillar,
                barPillars: barPillarSet,
                d3: {
                    options: {
                        bar: {
                            yDomain: [
                                0,
                                max<any>(
                                    barPillarSet.map((value) =>
                                        Math.max(value.percentToTarget, 0),
                                    ),
                                ),
                            ],
                        },
                    },
                    data: {
                        pie: [
                            {
                                x: pillar.thresholdTitle, // TODO revise
                                y: Math.min(
                                    Math.max(pillar.percentToTarget, 0),
                                    100,
                                ),
                                color: pillar.thresholdClass,
                            },
                            {
                                x: '', // TODO revise tl
                                y: Math.min(
                                    Math.max(100 - pillar.percentToTarget, 0),
                                    100,
                                ),
                                color: 'grey',
                            },
                        ],
                        bar: [
                            {
                                values: barPillarSet.map((value) => {
                                    return {
                                        x: value.barTitle,
                                        y: Math.max(value.percentToTarget, 0),
                                        color: value.thresholdClass,
                                    };
                                }),
                            },
                        ],
                    },
                },
            });
        }

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        return transformed.sort(
            (a, b) =>
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                (<DualChartPerformanceComparisonPillar>a.piePillar)
                    .kpiPosition -
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                (<DualChartPerformanceComparisonPillar>b.piePillar).kpiPosition,
        );
    }
}

export function validateDualChartPerformanceComparison(
    model: DualChartPerformanceComparison[],
): void {
    const valid =
        model != null &&
        model.length !== 0 &&
        model.find(
            (value) => value.piePillar == null || value.barPillars.length === 0,
        ) == null;
    if (!valid) {
        throw new Error('DualChartPerformanceComparison not valid');
    }
}
